import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { getReorderDestinationIndex } from "./edge";

// Keys used in the drag data. Nothing here knows about your items.
export const DRAGGABLE = "pdnd/draggable";
export const DRAGGABLE_TARGET = "pdnd/draggable-target";
export const DROPPABLE = "pdnd/droppable";

const DndContext = createContext(null);

export function useDndContext() {
  const ctx = useContext(DndContext);
  if (!ctx)
    throw new Error(
      "Droppable / Draggable must be used inside <DragDropContext>",
    );
  return ctx;
}

/**
 * <DragDropContext onDragStart={fn} onDragEnd={fn}>
 *
 * onDragEnd(result) is called with:
 * {
 *   draggableId,
 *   source:      { droppableId, index },
 *   destination: { droppableId, index } | null   // null = dropped outside
 * }
 *
 * destination.index is the FINAL index of the item (already adjusted for removal),
 * so you can splice it out of source and into destination directly.
 */
export function DragDropContext({ onDragStart, onDragEnd, children }) {
  const [instanceId] = useState(() => Symbol("dnd-context"));

  // droppableId -> Set(draggableId); used to know list length when dropping on empty space
  const registry = useRef(new Map());

  // always call the latest callbacks without re-subscribing the monitor
  const handlers = useRef({ onDragStart, onDragEnd });
  handlers.current = { onDragStart, onDragEnd };

  const register = useCallback((droppableId, draggableId) => {
    const map = registry.current;
    if (!map.has(droppableId)) map.set(droppableId, new Set());
    map.get(droppableId).add(draggableId);
    return () => {
      const set = map.get(droppableId);
      if (!set) return;
      set.delete(draggableId);
      if (set.size === 0) map.delete(droppableId);
    };
  }, []);

  useEffect(() => {
    const getCount = (droppableId) =>
      registry.current.get(droppableId)?.size ?? 0;

    return monitorForElements({
      canMonitor: ({ source }) =>
        source.data.type === DRAGGABLE && source.data.instanceId === instanceId,

      onDragStart: ({ source }) => {
        const sourceData = source.data;
        handlers.current.onDragStart?.({
          draggableId: sourceData.draggableId,
          source: { droppableId: sourceData.droppableId, index: sourceData.index },
        });
      },

      onDrop: ({ source, location }) => {
        const sourceData = source.data;
        const target = location.current.dropTargets[0]; // innermost
        handlers.current.onDragEnd?.({
          draggableId: sourceData.draggableId,
          source: { droppableId: sourceData.droppableId, index: sourceData.index },
          destination: getDestination(sourceData, target?.data, getCount),
        });
      },
    });
  }, [instanceId]);

  return (
    <DndContext.Provider value={{ instanceId, register }}>
      {children}
    </DndContext.Provider>
  );
}

function getDestination(source, target, getCount) {
  if (!target) return null;

  // Dropped on another draggable (above/below or left/right of it)
  if (target.type === DRAGGABLE_TARGET) {
    if (target.draggableId === source.draggableId) {
      return { droppableId: source.droppableId, index: source.index };
    }
    if (target.droppableId === source.droppableId) {
      return {
        droppableId: target.droppableId,
        index: getReorderDestinationIndex({
          startIndex: source.index,
          indexOfTarget: target.index,
          closestEdgeOfTarget: target.edge,
        }),
      };
    }
    const after = target.edge === "bottom" || target.edge === "right";
    return {
      droppableId: target.droppableId,
      index: after ? target.index + 1 : target.index,
    };
  }

  // Dropped on the droppable itself (empty list / empty space) -> end of list
  if (target.type === DROPPABLE) {
    const count = getCount(target.droppableId);
    const index =
      target.droppableId === source.droppableId
        ? Math.max(count - 1, 0)
        : count;
    return { droppableId: target.droppableId, index };
  }

  return null;
}
